import { create } from "zustand";

/**
 * Tiny session-scoped store for hidden debug flags. Each flag is meant to be
 * toggled by hand from the browser DevTools console and remembered for the
 * duration of the tab session via `sessionStorage`. Mirrors the
 * `debug_translations()` pattern in `src/config/i18n.ts`.
 *
 * Currently exposed:
 * - `refreshProductsEnabled` — when true, a per-row "refresh from catalog"
 *   button is shown in the insurances table on the update-account page,
 *   letting an admin wipe a row's parameters and re-pull defaults from the
 *   product-definition catalog.
 * - `productCatalogueNavVisible` — when true, the "Product catalog" button on
 *   the admin dashboard is shown and `/admin/product-definitions` is reachable;
 *   when false, that URL redirects to `/admin`. Toggle from the console:
 *   `debug.toggleProductCatalouge()`  // toggle
 *   `debug.toggleProductCatalouge(true)`  // force on
 *   `debug.toggleProductCatalouge(false)` // force off
 *
 * Usage in the console:
 *   debug_refresh_products()           // toggle
 *   debug_refresh_products(true)       // force on
 *   debug_refresh_products(false)      // force off
 */
interface DebugFlagsState {
	refreshProductsEnabled: boolean;
	setRefreshProductsEnabled: (next: boolean) => void;
	productCatalogueNavVisible: boolean;
	setProductCatalogueNavVisible: (next: boolean) => void;
}

const STORAGE_KEY = "debug_refresh_products";
const STORAGE_KEY_PRODUCT_CATALOGUE_NAV = "debug_product_catalogue_nav";

const readInitial = (): boolean => {
	try {
		return sessionStorage.getItem(STORAGE_KEY) === "true";
	} catch {
		return false;
	}
};

const readInitialProductCatalogueNav = (): boolean => {
	try {
		return sessionStorage.getItem(STORAGE_KEY_PRODUCT_CATALOGUE_NAV) === "true";
	} catch {
		return false;
	}
};

export const useDebugFlags = create<DebugFlagsState>((set) => ({
	refreshProductsEnabled: readInitial(),
	setRefreshProductsEnabled: (next) => {
		try {
			sessionStorage.setItem(STORAGE_KEY, next ? "true" : "false");
		} catch {
			// ignore storage errors — the flag still applies for this session
		}
		set({ refreshProductsEnabled: next });
	},
	productCatalogueNavVisible: readInitialProductCatalogueNav(),
	setProductCatalogueNavVisible: (next) => {
		try {
			sessionStorage.setItem(
				STORAGE_KEY_PRODUCT_CATALOGUE_NAV,
				next ? "true" : "false",
			);
		} catch {
			// ignore
		}
		set({ productCatalogueNavVisible: next });
	},
}));

const toggleRefreshProducts = (force?: boolean) => {
	const current = useDebugFlags.getState().refreshProductsEnabled;
	const next = typeof force === "boolean" ? force : !current;
	useDebugFlags.getState().setRefreshProductsEnabled(next);
	console.info(
		`[debug] refresh-products ${next ? "ENABLED" : "disabled"} — ` +
			(next
				? "per-row refresh buttons are now visible on the update-account page."
				: "per-row refresh buttons are hidden again."),
	);
	return next;
};

(
	window as unknown as { debug_refresh_products: typeof toggleRefreshProducts }
).debug_refresh_products = toggleRefreshProducts;

const toggleProductCatalouge = (force?: boolean) => {
	const current = useDebugFlags.getState().productCatalogueNavVisible;
	const next = typeof force === "boolean" ? force : !current;
	useDebugFlags.getState().setProductCatalogueNavVisible(next);
	console.info(
		`[debug] product-catalogue nav ${next ? "VISIBLE" : "hidden"} — ` +
			(next
				? "the Product catalog button is now shown on the admin dashboard."
				: "the Product catalog button is hidden again."),
	);
	return next;
};

type WindowDebug = {
	toggleProductCatalouge: typeof toggleProductCatalouge;
};

const w = window as unknown as { debug?: WindowDebug };
w.debug = { ...w.debug, toggleProductCatalouge };
